import { Alert, AlertDescription, AlertTitle, Button, Pagination } from '@noor/ui';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TABLE_CONTAINER_SELECTOR,
  useTableScrollReset,
} from '@/shared/lib/use-table-scroll-reset';
import {
  moderationSearchToFilter,
  useModerationList,
  type ChangeRequest,
  type ModerationEntityType,
  type ModerationListSearch,
  type Translate,
} from '@/entities/moderation';
import { DataTable } from '@/shared/ui/data-table';
import { filterRequests } from '../lib/filter-requests';
import { useModerationColumns } from './moderation-columns';

interface ModerationTableProps {
  entityType: ModerationEntityType;
  params: ModerationListSearch;
  onParamsChange: (next: Partial<ModerationListSearch>) => void;
  onSelect: (request: ChangeRequest) => void;
}

/**
 * Moderation queue table (Figma `Товары` frame): entity cell (name + SKU),
 * vendor, queue-specific middle column, single-line submitted date,
 * status pill, and the shared kit `Pagination` footer (page-size cluster,
 * totals caption, windowed numbers, circular icon-only steppers).
 *
 * Pagination is server-driven and URL-owned; `search` and the date range
 * filter client-side because the queue endpoint exposes no text query or
 * date filter. Row click opens the compare modal.
 */
export function ModerationTable({
  entityType,
  params,
  onParamsChange,
  onSelect,
}: ModerationTableProps) {
  const { t } = useTranslation('moderation');
  const translate = useCallback<Translate>(
    (key, fallback) => t(key, { defaultValue: fallback }),
    [t],
  );
  const tableCardRef = useTableScrollReset(
    params.page,
    params.pageSize,
    TABLE_CONTAINER_SELECTOR,
  );
  const { data, isLoading, isFetching, isError, refetch } = useModerationList(
    entityType,
    moderationSearchToFilter(params),
  );

  const rows = useMemo(() => filterRequests(data?.data ?? [], params), [data, params]);

  const columns = useModerationColumns(entityType, translate);
  const table = useReactTable({
    data: rows,
    columns,
    getCoreRowModel: getCoreRowModel(),
    // No client-side paging: otherwise a new `data` array auto-resets the page
    // index, which re-renders, which makes a new array — an endless render loop.
    manualPagination: true,
    getRowId: (request) => request.id,
  });
  const pageCount = Math.max(data?.meta.totalPages ?? 1, 1);
  const currentPage = Math.min(Math.max(params.page, 1), pageCount);

  return (
    <div
      className="flex min-h-0 flex-1 flex-col gap-4"
      data-testid="moderation-table"
      aria-busy={isFetching}
    >
      <div role="status" aria-live="polite" className="sr-only">
        {isFetching ? t('table.loading', 'Loading…') : t('table.loaded', 'Loaded')}
      </div>

      <DataTable
        ref={tableCardRef}
        table={table}
        isLoading={isLoading}
        className="min-h-0 flex-1"
        feedbackClassName="h-auto py-6"
        skeletonRowCount={params.pageSize}
        onRowClick={onSelect}
        errorContent={
          isError ? (
            <Alert variant="destructive">
              <AlertTitle>{t('table.error', 'Could not load the queue.')}</AlertTitle>
              <AlertDescription className="mt-2 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  size="modal"
                  shape="rounded"
                  variant="soft"
                  onClick={() => void refetch()}
                >
                  {t('table.retry', 'Try again')}
                </Button>
              </AlertDescription>
            </Alert>
          ) : undefined
        }
        emptyMessage={t('table.empty', 'Nothing waiting for review.')}
        emptyDescription={t('table.emptyHint', 'New change requests will appear here.')}
      />

      <Pagination
        page={currentPage}
        pageSize={params.pageSize}
        pageCount={pageCount}
        totalItems={data?.meta.total}
        onPageChange={(page) => onParamsChange({ page })}
        onPageSizeChange={(pageSize) => onParamsChange({ page: 1, pageSize })}
      />
    </div>
  );
}
